import { Button, Input, Select, Text, Textarea, toast } from "@medusajs/ui"
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Field } from "@/components/form"
import { FilePicker } from "@/components/file-picker"
import { ErrorState, SaveBar, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { useBrand, useSaveBrand, type BrandSettings } from "@/lib/settings"
import { SettingsGroup, SettingsTitle } from "./settings-general"

const IMAGE_URL = /^(https:\/\/[^\s"'<>()\\]+|http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?\/[^\s"'<>()\\]*|\/(?!\/)[^\s"'<>()\\]*)$/i
const HEX = /^#[0-9a-f]{6}$/i

type ImageKey = "logo" | "logo_dark" | "square_logo" | "favicon" | "email_item_image"
type ColorKey = "primary_color" | "accent_color" | "ink_color"

const IMAGES: { key: Exclude<ImageKey, "email_item_image">; label: string; hint: string; dark?: boolean; square?: boolean }[] = [
  { key: "logo", label: "Default logo", hint: "Used on light backgrounds: store header, checkout and emails. A wide PNG with a transparent background works best." },
  { key: "logo_dark", label: "Logo for dark backgrounds", hint: "Used in the footer and other dark sections.", dark: true },
  { key: "square_logo", label: "Square logo", hint: "Your mark on its own. Used for the home-screen icon and small placements. At least 180 × 180 px.", square: true },
  { key: "favicon", label: "Favicon", hint: "The icon in the browser tab. A 32 × 32 px PNG.", square: true },
]

const COLORS: { key: ColorKey; label: string; hint: string }[] = [
  { key: "primary_color", label: "Primary", hint: "Buttons, links and label bands." },
  { key: "ink_color", label: "Ink", hint: "Text, dark sections and the footer." },
  { key: "accent_color", label: "Accent", hint: "Small details only, never text or backgrounds." },
]

/** Settings → Brand, like Shopify's brand assets: logos, favicon, colours, slogan. */
export function BrandSettingsPage() {
  const { data, error } = useBrand()
  if (error) return <ErrorState error={error} />
  if (!data) return null
  return <BrandForm initial={data.brand} />
}

function ImageRow({ label, hint, value, dark, square, writable, onPick, onClear, onType }: { label: string; hint: string; value: string; dark?: boolean; square?: boolean; writable: boolean; onPick: () => void; onClear: () => void; onType: (v: string) => void }) {
  const bad = value !== "" && !IMAGE_URL.test(value)
  return (
    <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-start">
      <div className={`flex shrink-0 items-center justify-center overflow-hidden rounded-lg border border-ui-border-base ${dark ? "bg-[#1a1a1a]" : "bg-ui-bg-subtle"} ${square ? "h-20 w-20" : "h-20 w-full sm:w-56"}`}>
        {value && !bad ? <img src={value} alt="" className="max-h-full max-w-full object-contain p-2" /> : <Text size="xsmall" className={dark ? "text-white/60" : "text-ui-fg-muted"}>No image</Text>}
      </div>
      <div className="min-w-0 flex-1">
        <Field label={label} hint={bad ? "Use an https:// link or a /path on your store" : hint}>
          <Input value={value} maxLength={500} placeholder="Select from Files or paste a URL" disabled={!writable} aria-invalid={bad} onChange={(e) => onType(e.target.value.trim())} />
        </Field>
        {writable && (
          <div className="mt-2 flex gap-2">
            <Button type="button" size="small" variant="secondary" onClick={onPick}>Select from Files</Button>
            {value && <Button type="button" size="small" variant="transparent" onClick={onClear}>Remove</Button>}
          </div>
        )}
      </div>
    </div>
  )
}

function BrandForm({ initial }: { initial: BrandSettings }) {
  const writable = useCan()("settings:write")
  const base = useMemo(() => initial, [initial])
  const [b, setB] = useState(initial)
  const [picking, setPicking] = useState<ImageKey | null>(null)
  const save = useSaveBrand()
  useEffect(() => setB(base), [base])
  const dirty = JSON.stringify(b) !== JSON.stringify(base)
  const set = (p: Partial<BrandSettings>) => setB((x) => ({ ...x, ...p }))
  const itemImages = b.email_item_images ?? "product"
  const itemImage = b.email_item_image ?? ""
  // Emails can only load images from https:// addresses.
  const itemImageBad = itemImages === "custom" && !/^https:\/\/[^\s"'<>()\\]+$/i.test(itemImage)
  const invalid = IMAGES.some(({ key }) => b[key] !== "" && !IMAGE_URL.test(b[key])) || COLORS.some(({ key }) => b[key] !== "" && !HEX.test(b[key])) || itemImageBad
  const doSave = () => save.mutate(b, { onSuccess: () => toast.success("Brand saved"), onError: (e) => toast.error(errorMessage(e)) })

  return (
    <div className="pb-24">
      <SettingsTitle title="Brand" />
      <SettingsGroup title="Logos" description="Upload the files in Content → Files first, then select them here. Changes appear on your store right away.">
        <Section>
          <div className="divide-y divide-ui-border-base">
            {IMAGES.map((i) => (
              <ImageRow key={i.key} label={i.label} hint={i.hint} value={b[i.key]} dark={i.dark} square={i.square} writable={writable} onPick={() => setPicking(i.key)} onClear={() => set({ [i.key]: "" } as Partial<BrandSettings>)} onType={(v) => set({ [i.key]: v } as Partial<BrandSettings>)} />
            ))}
          </div>
        </Section>
        <Text size="xsmall" className="mt-2 text-ui-fg-subtle">
          <Link to="/content/files" className="text-ui-fg-interactive">Go to Files</Link> · The social sharing image is set in <Link to="/online-store" className="text-ui-fg-interactive">Online Store → Preferences</Link>.
        </Text>
      </SettingsGroup>

      <SettingsGroup title="Colors" description="Hex values. Leave blank to use the colours built into your store's design.">
        <Section>
          <div className="grid gap-4 px-5 py-4 sm:grid-cols-3">
            {COLORS.map(({ key, label, hint }) => {
              const v = b[key]
              const bad = v !== "" && !HEX.test(v)
              return (
                <Field key={key} label={label} hint={bad ? "Use a hex colour like #1649A8" : hint}>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label={`${label} colour picker`}
                      value={HEX.test(v) ? v : "#ffffff"}
                      disabled={!writable}
                      onChange={(e) => set({ [key]: e.target.value.toUpperCase() } as Partial<BrandSettings>)}
                      className="h-8 w-9 shrink-0 cursor-pointer rounded-md border border-ui-border-base bg-transparent p-0.5"
                    />
                    <Input value={v} maxLength={7} placeholder="#000000" disabled={!writable} aria-invalid={bad} onChange={(e) => set({ [key]: e.target.value.trim() } as Partial<BrandSettings>)} />
                  </div>
                </Field>
              )
            })}
          </div>
        </Section>
      </SettingsGroup>

      <SettingsGroup title="Slogan and short description">
        <Section>
          <div className="flex flex-col gap-4 px-5 py-4">
            <Field label="Slogan" hint={`${b.slogan.length} of 80 characters used`}>
              <Input value={b.slogan} maxLength={80} disabled={!writable} onChange={(e) => set({ slogan: e.target.value })} />
            </Field>
            <Field label="Short description" hint={`${b.short_description.length} of 150 characters used. Shown in the store footer.`}>
              <Textarea rows={2} value={b.short_description} maxLength={150} disabled={!writable} onChange={(e) => set({ short_description: e.target.value })} />
            </Field>
          </div>
        </Section>
      </SettingsGroup>

      <SettingsGroup title="Emails" description="How products look in order, shipping, subscription and cart emails.">
        <Section>
          <div className="flex flex-col gap-4 px-5 py-4">
            <Field label="Product images in emails" hint="Use “No image” or one image for every item while product photos show artwork you don't want in emails.">
              <Select value={itemImages} disabled={!writable} onValueChange={(v) => set({ email_item_images: v as "product" | "none" | "custom" })}>
                <Select.Trigger>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="product">Each product's photo</Select.Item>
                  <Select.Item value="none">No image</Select.Item>
                  <Select.Item value="custom">The same image for every item</Select.Item>
                </Select.Content>
              </Select>
            </Field>
          </div>
          {itemImages === "custom" && (
            <div className="border-t border-ui-border-base">
              <ImageRow
                label="Image for every item"
                hint={itemImageBad && itemImage ? "Emails need an https:// image (Content → Files gives you one)" : "A square image, at least 120 × 120 px. Shown next to every product in customer emails."}
                value={itemImage}
                square
                writable={writable}
                onPick={() => setPicking("email_item_image")}
                onClear={() => set({ email_item_image: "" })}
                onType={(v) => set({ email_item_image: v })}
              />
            </div>
          )}
        </Section>
      </SettingsGroup>

      {picking && (
        <FilePicker
          title="Select image"
          onClose={() => setPicking(null)}
          onDone={([file]) => {
            set({ [picking]: file.url } as Partial<BrandSettings>)
            setPicking(null)
          }}
        />
      )}
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => setB(base)} onSave={doSave} saving={save.isPending} disabled={invalid} />
    </div>
  )
}
